import { useDetailsDrawer } from '@src/components/DetailsDrawer/useDetailsDrawer';
import { orderSliceActions } from '@src/components/OMS/NewOrder/OrderSlice';
import {
  getOrderFormEligibilityForResubmit,
  getOrderFormEligibilityForSymbol,
  resubmitsViaSalesOrderForm,
} from '@src/components/OMS/NewOrder/orderFormSecurityEligibility';
import { rfqSliceActions } from '@src/components/OMS/NewRFQ/RFQSlice';
import { hideFeedbackCard, openView } from '@src/components/OMS/OMSSlice';
import { OMSView } from '@src/components/OMS/OMSView';
import { resubmitSalesOrderThunk } from '@src/components/OMS/SalesOrder/SalesOrderSlice';
import { mapOrderToCustomerOrder } from '@src/components/OMS/utils';
import { CancelSelectedDialog } from '@src/containers/Blotters/CancelSelectedDialog';
import { ResumeDialog } from '@src/containers/Blotters/Orders/Dialogs';
import { useFeatureFlag, useRoleAuth } from '@src/hooks';
import { useOrderInteractions } from '@src/providers';
import { useTradingSettings } from '@src/providers/AppConfigProvider';
import { useAppStateDispatch } from '@src/providers/AppStateProvider';
import { canResubmitPredictionClose } from '@src/utils/canResubmitPredictionClose';
import {
  useAssertOrderCancelable,
  useAssertOrderModifiable,
  useAssertOrderPausable,
  useAssertOrderResumable,
} from '@src/utils/order';
import {
  ACTION,
  Button,
  ButtonVariants,
  Flex,
  IconButton,
  IconName,
  isOrderComplete,
  MixpanelEvent,
  MixpanelEventProperty,
  MOBILE_MODAL_CONTENT_PORTAL,
  MobileModalSheetActionsContainer,
  Tooltip,
  useCallbackRef,
  useDisclosure,
  useMixpanel,
  useMobileModal,
  useSecuritiesContext,
  type Order,
} from '@talos/kyoko';

interface MobileOrderDetailFooterProps {
  order: Order;
}

/**
 * Action buttons rendered in the sticky footer of the mobile order detail sheet.
 *
 * Complete orders show: Resubmit, Archive (when enabled), Analytics (icon).
 * Active orders show: Cancel, Modify, Pause/Resume (icons, when applicable), Analytics (icon).
 *
 * Mirrors the logic in {@link OrderCard} but adapted for the mobile detail sheet context.
 */
export function MobileOrderDetailFooter({ order }: MobileOrderDetailFooterProps) {
  const mixpanel = useMixpanel();
  const dispatch = useAppStateDispatch();
  const { pause, cancel, resume, archive, unarchive } = useOrderInteractions();
  const { isAuthorized } = useRoleAuth();
  const { openDetailsDrawer } = useDetailsDrawer();
  const { closeModal } = useMobileModal();
  const { confirmOrderCancels, confirmOrderResume } = useTradingSettings();
  const { enableArchiveOrders } = useFeatureFlag();

  const cancelDialog = useDisclosure();
  const resumeDialog = useDisclosure();

  const isOrderModifiable = useAssertOrderModifiable();
  const isOrderCancelable = useAssertOrderCancelable();
  const isOrderPausable = useAssertOrderPausable();
  const isOrderResumable = useAssertOrderResumable();
  const { securitiesBySymbol } = useSecuritiesContext();
  const modifyFormEligibility = getOrderFormEligibilityForSymbol(order.Symbol, securitiesBySymbol);
  const resubmitFormEligibility = getOrderFormEligibilityForResubmit(order, securitiesBySymbol);

  const canModifyOrder = isOrderModifiable(order) && modifyFormEligibility.allowed;
  const canResubmitOrder = isAuthorized(ACTION.SUBMIT_ORDER) && resubmitFormEligibility.allowed;
  const canCancelOrder = isOrderCancelable(order);
  const canPauseOrder = isOrderPausable(order);
  const canResumeOrder = isOrderResumable(order);
  const canArchiveOrder = isAuthorized(ACTION.ARCHIVE_ORDER) && enableArchiveOrders && order.isArchivable;
  const canUnarchiveOrder = isAuthorized(ACTION.ARCHIVE_ORDER) && enableArchiveOrders && order.isUnArchivable;

  const handleResubmit = useCallbackRef(() => {
    if (!resubmitFormEligibility.allowed) {
      return;
    }
    mixpanel.track(MixpanelEvent.OpenResubmitOrder);
    if (order.QuoteID) {
      dispatch(openView(OMSView.RFQForm));
      dispatch(rfqSliceActions.resubmitRFQ(order));
    } else if (resubmitsViaSalesOrderForm(order)) {
      const customerOrder = mapOrderToCustomerOrder(order);
      dispatch(resubmitSalesOrderThunk(customerOrder));
      dispatch(openView(OMSView.SalesOrder));
    } else {
      dispatch(openView(OMSView.NewOrderForm));
      dispatch(orderSliceActions.resubmitOrder({ order }));
    }
    dispatch(hideFeedbackCard());
  });

  const handleArchive = useCallbackRef(() => {
    archive(order.OrderID);
  });
  const handleUnarchive = useCallbackRef(() => {
    unarchive(order.OrderID);
  });

  const handleCancel = useCallbackRef(() => {
    if (confirmOrderCancels) {
      cancelDialog.open();
    } else {
      mixpanel.track(MixpanelEvent.CancelOrder, { [MixpanelEventProperty.ID]: order.OrderID });
      cancel(order.OrderID);
      closeModal(); // cancelled orders drop out of the Open Orders set the sheet is showing
    }
  });

  const handleModify = useCallbackRef(() => {
    if (!modifyFormEligibility.allowed) {
      return;
    }
    mixpanel.track(MixpanelEvent.ModifyOrder);
    dispatch(hideFeedbackCard());
    dispatch(openView(OMSView.NewOrderForm));
    dispatch(orderSliceActions.modifyOrder(order));
  });

  const handleResume = useCallbackRef(() => {
    mixpanel.track(MixpanelEvent.ResumeOrder);
    if (confirmOrderResume) {
      resumeDialog.open();
    } else {
      resume(order.OrderID);
    }
  });

  const handlePause = useCallbackRef(() => {
    mixpanel.track(MixpanelEvent.PauseOrder);
    pause(order.OrderID);
  });

  const handleAnalytics = useCallbackRef(() => {
    mixpanel.track(MixpanelEvent.OpenOrderAnalytics);
    openDetailsDrawer({ entity: order });
  });

  const handleConfirmCancel = useCallbackRef((orders: Order[]) => {
    orders.forEach(o => {
      mixpanel.track(MixpanelEvent.CancelOrder, { [MixpanelEventProperty.ID]: o.OrderID });
      cancel(o.OrderID);
    });
    // A cancelled order drops out of the Open Orders set the sheet is showing, so there's nothing left to display.
    closeModal();
  });

  const isComplete = isOrderComplete(order.OrdStatus);
  const showResubmit = isComplete && canResubmitPredictionClose(order);

  return (
    <>
      {/* portalID keeps these dialogs inside the sheet's own portal, so its outside-click-to-close doesn't fire on confirm/cancel clicks */}
      <CancelSelectedDialog
        onConfirm={handleConfirmCancel}
        selectedItems={[order]}
        cancelSelectedDialog={{ ...cancelDialog, portalID: MOBILE_MODAL_CONTENT_PORTAL }}
      />
      <ResumeDialog selectedOrder={order} resumeDialog={{ ...resumeDialog, portalID: MOBILE_MODAL_CONTENT_PORTAL }} />

      <MobileModalSheetActionsContainer>
        {isComplete ? (
          <>
            {showResubmit ? (
              <Flex flex={1}>
                <Tooltip
                  tooltip={resubmitFormEligibility.reason}
                  disabled={resubmitFormEligibility.allowed}
                  targetStyle={{ width: '100%' }}
                >
                  <Button
                    width="100%"
                    pill
                    variant={ButtonVariants.Default}
                    disabled={!canResubmitOrder}
                    onClick={handleResubmit}
                  >
                    Resubmit
                  </Button>
                </Tooltip>
              </Flex>
            ) : null}
            {canArchiveOrder && (
              <Button flex={1} pill variant={ButtonVariants.Default} onClick={handleArchive}>
                Archive
              </Button>
            )}
            {canUnarchiveOrder && (
              <Button flex={1} pill variant={ButtonVariants.Default} onClick={handleUnarchive}>
                Unarchive
              </Button>
            )}
          </>
        ) : (
          <>
            <Button
              data-testid="mobile-order-detail-cancel-button"
              pill
              flex={1}
              variant={ButtonVariants.Default}
              disabled={!canCancelOrder}
              onClick={handleCancel}
            >
              Cancel
            </Button>
            <Flex flex={1}>
              <Tooltip
                tooltip={modifyFormEligibility.reason}
                disabled={modifyFormEligibility.allowed}
                targetStyle={{ width: '100%' }}
              >
                <Button
                  data-testid="mobile-order-detail-modify-button"
                  pill
                  width="100%"
                  variant={ButtonVariants.Default}
                  disabled={!canModifyOrder}
                  onClick={handleModify}
                >
                  Modify
                </Button>
              </Tooltip>
            </Flex>
            {canPauseOrder && (
              <IconButton
                round
                disabled={!!order.ParentOrderID || !isAuthorized(ACTION.PAUSE_ORDER)}
                icon={IconName.Pause}
                variant={ButtonVariants.Primary}
                onClick={handlePause}
              />
            )}
            {canResumeOrder && (
              <IconButton
                data-testid="mobile-order-detail-resume-button"
                round
                disabled={!!order.ParentOrderID || !isAuthorized(ACTION.RESUME_ORDER)}
                icon={IconName.Play}
                variant={ButtonVariants.Default}
                onClick={handleResume}
              />
            )}
          </>
        )}
        <IconButton
          data-testid="mobile-order-detail-analytics-button"
          round
          variant={ButtonVariants.Default}
          icon={IconName.ChartLine}
          onClick={handleAnalytics}
        />
      </MobileModalSheetActionsContainer>
    </>
  );
}
